import { useMemo, useRef, useState, useEffect } from "react";
import CodeMirror, { type Extension } from "@uiw/react-codemirror";
import { materialDark, materialLight } from "@uiw/codemirror-theme-material";
import {
  EditorView,
  keymap,
  highlightActiveLine,
  highlightActiveLineGutter,
} from "@codemirror/view";
import { Prec } from "@codemirror/state";
import { search, searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import { toggleComment } from "@codemirror/commands";
import { languageExtFor } from "../utils/languageInfo";
import { inlineDiff } from "./InlineDiffExtension";

interface CursorInfo {
  line: number;
  col: number;
  selection: number;
}

interface Props {
  path: string;
  content: string;
  onChange: (next: string) => void;
  onSave?: () => void;
  onCursor?: (info: CursorInfo) => void;
  onReady?: (view: EditorView) => void;
  readOnly?: boolean;
  wrapLines?: boolean;
}

// Font stack mirrors VS Code / Cursor: prefer JetBrains Mono / SF Mono /
// Menlo with ligatures via font-feature-settings. The host doesn't have
// to install any font — the stack falls through to the OS monospace
// face. Line height 1.55 matches Cursor's density.
const editorTheme = EditorView.theme({
  "&": {
    fontFamily:
      "'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
    fontSize: "13px",
    fontFeatureSettings: "'liga' 1, 'calt' 1",
    // Bound the editor to its host so long files scroll (see .cm-scroller).
    height: "100%",
  },
  ".cm-content": {
    fontFamily: "inherit",
    lineHeight: "1.55",
    caretColor: "var(--cm-caret, #4a90e2)",
  },
  ".cm-gutters": {
    fontSize: "12px",
    backgroundColor: "transparent",
    borderRight: "1px solid var(--line, rgba(255,255,255,0.06))",
  },
  ".cm-activeLineGutter, .cm-activeLine": {
    backgroundColor: "rgba(120, 160, 255, 0.06)",
  },
  ".cm-selectionMatch": {
    backgroundColor: "rgba(120, 160, 255, 0.18)",
    outline: "1px solid rgba(120, 160, 255, 0.35)",
  },
  // The native search panel uses generic input styling; align it to the
  // app's dark palette so it doesn't look like a 1996 popup.
  ".cm-panels": {
    backgroundColor: "var(--panel, #161922)",
    color: "var(--ink, #e6e8f0)",
    borderTop: "1px solid var(--line, rgba(255,255,255,0.08))",
  },
  ".cm-panel input": {
    backgroundColor: "var(--field-bg, #0f1218)",
    color: "var(--field-ink, #e6e8f0)",
    border: "1px solid var(--line, rgba(255,255,255,0.12))",
    borderRadius: "4px",
    padding: "2px 6px",
  },
  ".cm-panel button": {
    backgroundColor: "transparent",
    color: "var(--ink, #e6e8f0)",
    border: "1px solid var(--line, rgba(255,255,255,0.12))",
    borderRadius: "4px",
    padding: "2px 8px",
    cursor: "pointer",
  },
  ".cm-panel button:hover": {
    backgroundColor: "rgba(120, 160, 255, 0.12)",
  },
  // Let the scroller handle overflow so long files scroll instead of being
  // clipped by the overflow:hidden host.
  ".cm-scroller": {
    overflow: "auto",
  },
});

export function FileEditor({
  path,
  content,
  onChange,
  onSave,
  onCursor,
  onReady,
  readOnly,
  wrapLines,
}: Props) {
  // Refs let the useMemo capture stable function identities while always
  // calling the latest callbacks — avoids costly editor re-init on every render.
  const onSaveRef = useRef(onSave);
  const onCursorRef = useRef(onCursor);
  onSaveRef.current = onSave;
  onCursorRef.current = onCursor;

  // Track the document theme via MutationObserver so toggling the topbar
  // theme re-renders the editor surface without waiting for a tab switch.
  const [themeKey, setThemeKey] = useState<string | undefined>(() =>
    typeof document !== "undefined" ? document.documentElement.dataset.theme : undefined
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setThemeKey(document.documentElement.dataset.theme);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const docTheme = themeKey === "light" ? materialLight : materialDark;

  const extensions = useMemo(() => {
    const lang = languageExtFor(path);
    const list: Extension[] = [
      editorTheme,
      highlightActiveLine(),
      highlightActiveLineGutter(),
      search({ top: true }),
      highlightSelectionMatches(),
      // Prec.highest so save always wins over any basicSetup bindings.
      // searchKeymap is re-added here because basicSetup's copy is disabled
      // below; spreading it after Mod-s keeps all search shortcuts active.
      Prec.highest(
        keymap.of([
          {
            key: "Mod-s",
            preventDefault: true,
            run: () => {
              onSaveRef.current?.();
              return true;
            },
          },
          { key: "Mod-/", run: toggleComment },
          ...searchKeymap,
        ])
      ),
      EditorView.updateListener.of((update) => {
        if (!onCursorRef.current) return;
        if (!update.selectionSet && !update.docChanged) return;
        const head = update.state.selection.main.head;
        const lineInfo = update.state.doc.lineAt(head);
        const selectionLen = update.state.selection.main.to - update.state.selection.main.from;
        onCursorRef.current({
          line: lineInfo.number,
          col: head - lineInfo.from + 1,
          selection: selectionLen,
        });
      }),
    ];
    list.push(inlineDiff());
    if (wrapLines) list.push(EditorView.lineWrapping);
    if (lang) list.unshift(lang);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, wrapLines]);

  return (
    <CodeMirror
      className="editor-codemirror"
      // @uiw/react-codemirror defaults to height:auto (grows to content), so
      // the editor outgrew the overflow:hidden host and couldn't scroll. Pin it
      // to 100% of the host so .cm-scroller (overflow:auto) handles vertical
      // scrolling for long files.
      height="100%"
      value={content}
      theme={docTheme}
      extensions={extensions}
      readOnly={readOnly}
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        bracketMatching: true,
        closeBrackets: true,
        autocompletion: true,
        // We provide our own highlightActiveLine extensions above so we can
        // control their order; setting them false here avoids double-wiring.
        highlightActiveLine: false,
        highlightActiveLineGutter: false,
        indentOnInput: true,
        // basicSetup's default Mod-s keymap conflicts with our save handler.
        // Disabling its searchKeymap would also drop Ctrl-F; instead we add
        // `search()` ourselves above (which brings its own keymap) and let
        // our Prec.highest save binding override.
        searchKeymap: false,
      }}
      onChange={(value) => onChange(value)}
      onCreateEditor={(view) => {
        onReady?.(view);
        // Test seam: integration tests retrieve the view from the DOM
        // node. No runtime consumers; invisible outside vitest.
        (view.dom as unknown as Record<string, unknown>).__cmView = view;
      }}
    />
  );
}
